import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormikCreatableField from "components/Common/FormikCreatableField"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import { toLocalDatetimeInput } from "utils/formatDate"
import {
    useCreatePettyCashBill,
    useEditPettyCashBill,
} from "hooks/usePettyCashBills"

const PettyCashBillAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  bill,
  optionLoading,
  paymentForsData,
  paymentFromsData,
  vehiclesData,
  submittedData,
}) => {
  const { mutateAsync: createBill, isPending: createLoading } =
    useCreatePettyCashBill()
  const { mutateAsync: editBill, isPending: editLoading } =
    useEditPettyCashBill()

  const validationSchema = Yup.object({
    bill_date: Yup.date().required("Please select bill date"),
    submitted_by: Yup.mixed().nullable(),
    vehicle_id: Yup.mixed().nullable(),
    remarks: Yup.string().nullable(),
    items: Yup.array()
      .of(
        Yup.object({
          payment_for_id: Yup.mixed()
            .nullable()
            .required("Payment For is required"),
          amount: Yup.number()
            .min(1, "Amount must be >= 1")
            .required("Amount is required"),
          description: Yup.string().nullable(),
        })
      )
      .min(1, "At least one bill item is required"),
  })

  const initialValues = useMemo(
    () => ({
      bill_date: isEdit
        ? bill?.bill_date || toLocalDatetimeInput()
        : toLocalDatetimeInput(),
      submitted_by: bill?.submitted_by || "",
      vehicle_id: bill?.vehicle
        ? {
            label: `${bill.vehicle.stock_id} - ${
              bill.vehicle.full_chassis || ""
            }`,
            value: bill.vehicle.id,
          }
        : null,
      remarks: bill?.remarks || "",
      items: bill?.items?.map(i => ({
        payment_for_id: i.paymentFor
          ? { label: i.paymentFor.name, value: i.paymentFor.id }
          : null,
        description: i.description || "",
        amount: i.amount || "",
      })) || [{ payment_for_id: null, description: "", amount: "" }],
    }),
    [bill, isEdit]
  )

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        vehicle_id: values.vehicle_id?.value || values.vehicle_id,
        items: values.items.map(i => ({
          payment_for_id: i.payment_for_id?.value || i.payment_for_id,
          description: i.description,
          amount: i.amount,
        })),
      }

      const action = isEdit
        ? editBill({ id: bill.id, ...payload })
        : createBill(payload)

      try {
        const res = await action
        toast.success(
          res?.message ?? `Bill ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save bill.")
      }
    },
    [isEdit, bill, toggleModal, createBill, editBill]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = createLoading || editLoading
    return `${isEdit ? "Save Changes" : "Add Bill"}${loading ? "..." : ""}`
  }, [isEdit, createLoading, editLoading])

  const isSubmitDisabled = useMemo(
    () => optionLoading || createLoading || editLoading,
    [optionLoading, createLoading, editLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Bill" : "Add Bill"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Row className="mb-3">
          <Col sm={6}>
            <Label htmlFor="bill_date">Bill Date</Label>
            <InputField
              type="datetime-local"
              name="bill_date"
              id="bill_date"
              value={formik.values.bill_date}
              onChange={formik.handleChange}
              invalid={formik.touched.bill_date && !!formik.errors.bill_date}
              loading={optionLoading}
            />
            {formik.touched.bill_date && formik.errors.bill_date && (
              <FormFeedback type="invalid">
                {formik.errors.bill_date}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6}>
            <FormikCreatableField
              name="submitted_by"
              label="Submitted By"
              formik={formik}
              data={submittedData}
              placeholder="Select a submitter"
            />
            {formik.touched.submitted_by && formik.errors.submitted_by && (
              <FormFeedback type="invalid">
                {formik.errors.submitted_by}
              </FormFeedback>
            )}
          </Col>

          <Col sm={12} className="mb-3">
            <VehicleIdSelect formik={formik} data={vehiclesData} />
          </Col>
        </Row>

        <hr />
        <h6>Bill Items</h6>
        {formik.values.items.map((item, idx) => (
          <Row key={idx} className="mb-2">
            <Col sm={4}>
              <FormikCreatableField
                name={`items[${idx}].payment_for_id`}
                formik={formik}
                data={paymentForsData}
                placeholder="Payment For"
              />
              {formik.touched.items?.[idx]?.payment_for_id &&
                formik.errors.items?.[idx]?.payment_for_id && (
                  <FormFeedback type="invalid">
                    {formik.errors.items[idx].payment_for_id}
                  </FormFeedback>
                )}
            </Col>
            <Col sm={4}>
              <InputField
                type="text"
                placeholder="Description"
                name={`items[${idx}].description`}
                value={formik.values.items[idx].description}
                onChange={formik.handleChange}
              />
            </Col>
            <Col sm={3}>
              <InputField
                type="number"
                placeholder="Amount"
                name={`items[${idx}].amount`}
                value={formik.values.items[idx].amount}
                onChange={formik.handleChange}
              />
            </Col>
            <Col sm={1}>
              <Button
                color="danger"
                onClick={() => {
                  const newItems = formik.values.items.filter(
                    (_, i) => i !== idx
                  )
                  formik.setFieldValue("items", newItems)
                }}
              >
                X
              </Button>
            </Col>
          </Row>
        ))}
        <Button
          color="primary"
          className="mt-2"
          onClick={() => {
            formik.setFieldValue("items", [
              ...formik.values.items,
              { payment_for_id: null, description: "", amount: "" },
            ])
          }}
        >
          + Add Item
        </Button>

        <div className="mb-3 mt-3">
          <Label htmlFor="remarks">Remarks</Label>
          <InputField
            type="textarea"
            name="remarks"
            id="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            invalid={formik.touched.remarks && !!formik.errors.remarks}
          />
          {formik.touched.remarks && formik.errors.remarks && (
            <FormFeedback type="invalid">{formik.errors.remarks}</FormFeedback>
          )}
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default PettyCashBillAddEdit
